<template>
  <li class="action-menu__item-wrapper" @click="preventDoubleClick">
    <component
      :is="href ? 'a' : 'button'"
      class="action-menu__item"
      :href="href"
      target="_blank"
      :disabled="isDisabled"
    >
      <span v-if="icon" class="iconfont" :class="icon"></span>
      <slot></slot>
    </component>
  </li>
</template>

<script setup lang="ts">
import { onUnmounted, ref } from 'vue'

/**
 * This component is an item inside an action menu dropdown menu.
 * It's meant to be used inside the default slot of the `ActionMenu` component.
 *
 * @param icon - An iconfont icon class name.
 * @param href - A link. Passing this prop converts the item
 *   from a button into a link (`<a>`).
 */
defineProps<{
  icon?: string
  href?: string
}>()

const isDisabled = ref(false)
const disabledTimeout = ref<number | null>(null)

function preventDoubleClick(): void {
  isDisabled.value = true

  disabledTimeout.value = setTimeout(() => {
    isDisabled.value = false
  }, 500)
}

onUnmounted(() => {
  if (disabledTimeout.value) {
    clearTimeout(disabledTimeout.value)
  }
})
</script>

<style lang="scss" scoped>
.action-menu {
  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 16px;
    line-height: 1.25;
    color: $c-black-2;
    transition: all 0.2s;
    @include hover {
      background: rgba($c-black-2, 0.05);
    }
    @media (max-width: $w-medium-s) {
      font-size: 14px;
      gap: 4px;
      padding: 6px 8px;
    }

    .iconfont {
      font-size: 1.5em;
      line-height: 1;
    }
  }

  &__item-wrapper {
    margin: 0;
    padding: 0;
  }
}

.night-mode .action-menu {
  &__item {
    color: $c-black-11;
    @include hover {
      background: rgba($c-dark-3, 0.5);
    }
  }
}
</style>
